import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ChangeRequestView } from '../../types.js'
import { CHANGE_PARAM, CHANGES_PATH } from './changeText.js'

export const CHANGES_QUERY_KEY = ['admin', 'changes'] as const
export const changeQueryKey = (changeRequestId: string) => [...CHANGES_QUERY_KEY, changeRequestId] as const
const FEEDBACK_QUERY_KEY = ['admin', 'feedback'] as const

/**
 * One step of a change (save, submit, a gate, a closure): every one answers
 * the change as it now stands, which replaces the cached copy at once; the
 * list and the feedback page are read again, since a closure frees the
 * linked feedback and an acceptance completes it.
 */
export function useChangeStep(onDone?: (change: ChangeRequestView) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (run: () => Promise<ChangeRequestView>) => run(),
    onSuccess: (change) => {
      queryClient.setQueryData(changeQueryKey(change.changeRequestId), change)
      void queryClient.invalidateQueries({ queryKey: CHANGES_QUERY_KEY, exact: true })
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY })
      onDone?.(change)
    },
  })
}

function readSelected(): string | null {
  const value = new URLSearchParams(window.location.search).get(CHANGE_PARAM)
  return value && value.trim() !== '' ? value : null
}

/**
 * The change open on the page, kept in the address as `?change=<id>` so a
 * link (Develop, the feedback row's chip) opens it and a reload keeps it.
 * Choosing replaces the address rather than adding to the history, so the
 * shell's "Back to Feedback" still steps back to where the admin came from.
 */
export function useSelectedChange(): [string | null, (changeRequestId: string | null) => void] {
  const [selected, setSelected] = useState<string | null>(readSelected)

  useEffect(() => {
    function follow(): void {
      if (window.location.pathname.replace(/\/$/, '') === CHANGES_PATH) {
        setSelected(readSelected())
      }
    }
    window.addEventListener('popstate', follow)
    return () => window.removeEventListener('popstate', follow)
  }, [])

  const choose = useCallback((changeRequestId: string | null) => {
    const search = changeRequestId ? `?${CHANGE_PARAM}=${encodeURIComponent(changeRequestId)}` : ''
    window.history.replaceState(window.history.state, '', `${CHANGES_PATH}${search}`)
    setSelected(changeRequestId)
  }, [])

  return [selected, choose]
}
